import { useMemo } from 'react';
import { Box, Button, Stack } from '@mui/material';
import {
  Controller,
  FieldValues,
  FormProvider,
  UseFormReturn
} from 'react-hook-form';
import { Blocker, LimitedTextField, NumberTextField } from 'components';
import {
  AdvancedDesignationsSelector,
  SelectorOption
} from 'components/AdvancedDesignationsSelector';
import Breadcrumbs, { Breadcrumb } from 'components/Breadcrumbs';
import ComboCheckBox from 'components/ComboCheckBox';
import DatePicker from 'components/DatePicker';
import Text from 'components/Text';
import { useOrgFeatures } from 'hooks/useOrgFeatures';
import { OrganizationRole, useRenderIfRole } from 'hooks/useRenderIfRole';
import { useDesignationGroups, useDesignations } from 'queries/UseDesignations';
import { mapDesignations } from 'utils/designations';
import { useAppContext } from '../../../hooks/useAppContext';
import { ICampaignSchema } from './Campaign.Schema';
import './CampaignEditForm.scss';

type CampaignEditFormProps = {
  methods: UseFormReturn<ICampaignSchema, any>;
  breadcrumbs: Breadcrumb[];
  formTitle: string;
  buttonLabel: string;
  isLoading: boolean;
  onSubmit: (data: FieldValues) => void;
};

export const CampaignEditForm = ({
  methods,
  breadcrumbs,
  formTitle,
  buttonLabel,
  isLoading,
  onSubmit
}: CampaignEditFormProps): JSX.Element => {
  const { renderIfRole } = useRenderIfRole();
  const { selectedOrganization } = useAppContext();
  const orgFeatures = useOrgFeatures();

  const { data: designations, isLoading: designationsIsLoading } =
    useDesignations({
      organizationId: selectedOrganization.id
    });

  const { data: unmappedDesignationGroups, isLoading: groupsIsLoading } =
    useDesignationGroups({ organizationId: selectedOrganization.id });

  const mappedDesignations = useMemo(() => {
    if (designations?.length > 0) {
      return designations.map((designation) => mapDesignations(designation));
    }
    return [];
  }, [designations]);

  const ungroupedDesignations: SelectorOption[] = useMemo(() => {
    if (designationsIsLoading || groupsIsLoading) return [];
    if (unmappedDesignationGroups?.length > 0) {
      return designations
        .filter((d) => !d.groups?.length)
        .map((d) => ({
          label: d.title,
          value: d.id,
          type: 'individual'
        }));
    }

    return designations.map((d) => ({
      label: d.title,
      value: d.id,
      type: 'individual'
    }));
  }, [
    designations,
    unmappedDesignationGroups,
    designationsIsLoading,
    groupsIsLoading
  ]);

  const mappedDesignationGroups: SelectorOption[] = useMemo(() => {
    if (groupsIsLoading || unmappedDesignationGroups?.length === 0) return [];

    return unmappedDesignationGroups.map((d) => ({
      label: d.name,
      value: d.id,
      type: 'group',
      designationsInGroup: d.designations
    }));
  }, [unmappedDesignationGroups, groupsIsLoading]);

  const useAdvancedDesignations = useMemo(
    () =>
      orgFeatures?.advancedDesignations &&
      unmappedDesignationGroups?.length > 0,
    [orgFeatures, unmappedDesignationGroups?.length]
  );

  return (
    <FormProvider {...methods}>
      <form
        className="campaign-form"
        onSubmit={methods.handleSubmit((data) => onSubmit(data))}
      >
        <Box className="campaign-form-header">
          <Stack
            className="campaign-form-header-stack"
            alignItems="center"
            spacing={1.5}
          >
            <div className="campaign-form-breadcrumbs">
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </div>
            <Text variant="h2" color="primary">
              {formTitle}
            </Text>
            <Text variant="h1">What are the details of your campaign?</Text>
          </Stack>
        </Box>
        <Blocker block={isLoading || groupsIsLoading || designationsIsLoading}>
          <Stack className="campaign-form-form" alignItems="center">
            <Stack direction="row" spacing={1} justifyContent="center">
              <Stack
                className="campaign-form-field-labels"
                spacing={2.8125}
                alignItems="flex-end"
              >
                <Text variant="h3">My campaign is named</Text>
                <Text variant="h3">and I want to raise</Text>
                <Text variant="h3">between</Text>
                <Text variant="h3">for</Text>
              </Stack>
              <Stack className="campaign-form-fields" spacing={1}>
                <Controller
                  name="title"
                  defaultValue=""
                  control={methods.control}
                  render={({
                    field: { ref, ...field },
                    fieldState: { error }
                  }) => (
                    <LimitedTextField
                      {...field}
                      maxChar={100}
                      hiddenLabel
                      placeholder="Enter Campaign Name"
                      error={!!error}
                      helperText={error?.message ?? null}
                    />
                  )}
                />
                <Controller
                  name="goalAmount"
                  defaultValue={null}
                  control={methods.control}
                  render={({
                    field: { ref, ...field },
                    fieldState: { error }
                  }) => (
                    <NumberTextField
                      {...field}
                      hiddenLabel
                      valueIsNumericString
                      thousandSeparator
                      prefix="$"
                      placeholder="$ Enter Goal Amount"
                      error={!!error}
                      helperText={error?.message ?? null}
                    />
                  )}
                />
                <Stack direction="row" spacing={0.75} alignItems="flex-start">
                  <Controller
                    name="startDate"
                    control={methods.control}
                    defaultValue={new Date().toString()}
                    render={({
                      field: { ref, onChange, ...field },
                      fieldState: { error }
                    }) => (
                      <DatePicker
                        value={field.value}
                        error={!!error}
                        onChange={(...args) => {
                          onChange(...args);
                          methods.trigger('startDate');
                        }}
                        TextFieldProps={{
                          ...field,
                          helperText: error?.message ?? null
                        }}
                      />
                    )}
                  />
                  <Text variant="h3" className="campaign-form-date-divider">
                    -
                  </Text>
                  <Controller
                    name="endDate"
                    control={methods.control}
                    render={({
                      field: { ref, onChange, ...field },
                      fieldState: { error }
                    }) => (
                      <DatePicker
                        value={field.value}
                        error={!!error}
                        minDate={new Date()}
                        label="No End Date"
                        onChange={(...args) => {
                          onChange(...args);
                          methods.trigger('startDate');
                          methods.trigger('endDate');
                        }}
                        TextFieldProps={{
                          ...field
                        }}
                      />
                    )}
                  />
                </Stack>
                <Controller
                  name="designations"
                  defaultValue={[]}
                  render={({
                    field: { ref, onBlur, value, ...field },
                    fieldState: { error }
                  }) =>
                    useAdvancedDesignations ? (
                      <AdvancedDesignationsSelector
                        {...field}
                        ungroupedDesignations={ungroupedDesignations}
                        designationGroups={mappedDesignationGroups}
                        error={!!error}
                        onBlur={async () => {
                          await methods.trigger('designations');
                        }}
                        helperText={error?.message ?? null}
                        defaultValue={value}
                      />
                    ) : (
                      <ComboCheckBox
                        {...field}
                        options={
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          mappedDesignations?.map((d: any) => ({
                            label: d.title,
                            value: d.id,
                            type: 'individual'
                          })) ?? []
                        }
                        defaultValue={value}
                        error={!!error}
                        onBlur={async () => {
                          await methods.trigger('designations');
                        }}
                        helperText={error?.message ?? null}
                        label={(numSelected) =>
                          `(${numSelected}) Designation${
                            numSelected === 1 ? '' : 's'
                          }`
                        }
                      />
                    )
                  }
                />
              </Stack>
            </Stack>
            <div className="campaign-form-button-container">
              {renderIfRole(
                <Button
                  fullWidth
                  type="submit"
                  variant="primary"
                  disabled={!methods.formState.isValid}
                >
                  {buttonLabel}
                </Button>,
                OrganizationRole.Editor
              )}
            </div>
          </Stack>
        </Blocker>
      </form>
    </FormProvider>
  );
};
